import {AccountInfo, AuthenticationResult, InteractionRequiredAuthError} from '@azure/msal-browser';
import axios from 'axios';
import {authScopes, publicClientApplication} from 'src/app/constants/config/authConfig';
import {config} from 'src/app/constants/config/config';
import {isDevBypassEnabled} from 'src/app/constants/config/devBypassConfig';

interface ApiConfiguration {
    baseUrl: string;
    tokenResolver?: () => string;
    key?: string;
}

class Api {
    public config: ApiConfiguration = {
        baseUrl: '',
    };

    public init(config: ApiConfiguration) {
        this.config = config;
    }

    public getBaseUrl() {
        return this.config.baseUrl;
    }

    public getToken() {
        if (!this.config.tokenResolver) return '';

        return this.config.tokenResolver();
    }
}

const apiInternal = new Api();
const apiStoplight = new Api();
const apiTicketManagement = new Api();

/** Initialize all API instances; should be executed as early as possible in the App. */
export function initApis() {
    apiInternal.init({
        baseUrl: config.INTERNAL_API_URL || '',
    });

    apiTicketManagement.init({
        baseUrl: config.TICKET_MANAGEMENT_API_URL || '',
    });

    apiStoplight.init({
        baseUrl: config.STOPLIGHT_MOCK_API,
    });
}

const idTokenExpiryBufferMs = 5 * 60 * 1000;

/**
 * Cached value of the last MSAL result, shared by every axios instance created via
 * getAxios (which is called once per service call). MSAL's own cache cannot serve us here:
 * with an empty scopes array acquireTokenSilent bypasses its cache and hits the /token
 * endpoint on every call (https://github.com/AzureAD/microsoft-authentication-library-for-js/issues/6887),
 * and its cache lookup also never checks the ID token's expiry (only the access token's),
 * while we send the ID token as Bearer.
 */
let cachedAuthResult: AuthenticationResult | null = null;

/** Single-flight guard: concurrent requests await the same acquisition instead of each firing
 * their own /token call (relevant at app startup when many queries run at once). */
let inFlightAcquisition: Promise<AuthenticationResult> | null = null;

function isIdTokenUsable(result: AuthenticationResult, account: AccountInfo): boolean {
    if (result.account?.homeAccountId !== account.homeAccountId) {
        return false;
    }

    const exp = (result.idTokenClaims as { exp?: number }).exp;

    if (!exp) {
        return false;
    }

    return exp * 1000 > (Date.now() + idTokenExpiryBufferMs);
}

async function acquireIdToken(): Promise<string> {
    const accounts = publicClientApplication.getAllAccounts();

    if (accounts.length === 0) {
        throw new Error('No authenticated user found');
    }

    const account = accounts[0];

    if (cachedAuthResult && isIdTokenUsable(cachedAuthResult, account)) {
        return cachedAuthResult.idToken;
    }

    if (!inFlightAcquisition) {
        const tokenRequest = {
            scopes: authScopes,
            account,
        };

        inFlightAcquisition = publicClientApplication
            .acquireTokenSilent(tokenRequest)
            .then((response) => {
                cachedAuthResult = response;
                return response;
            })
            .catch(async (error) => {
                cachedAuthResult = null;

                if (error instanceof InteractionRequiredAuthError) {
                    await publicClientApplication.acquireTokenRedirect(tokenRequest);
                }
                throw error;
            })
            .finally(() => {
                inFlightAcquisition = null;
            });
    }

    return (await inFlightAcquisition).idToken;
}

function getAxios(api: Api) {
    const instance = axios.create({
        baseURL: api.getBaseUrl(),
    });

    instance.interceptors.request.use(
        async (config) => {
            if (isDevBypassEnabled) {
                return config;
            }

            config.headers.Authorization = `Bearer ${await acquireIdToken()}`;
            return config;
        }
    );

    return instance;
}

export const getInternalAxiosInstance = () => getAxios(apiInternal);
export const getTicketManagementAxiosInstance = () => getAxios(apiTicketManagement);
